import { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Fallback usado apenas se a API não responder
const fallbackPartners = [
  { name: "MJ Moreira", logo: "/assets/img/partner-mjmoreira.webp" },
  { name: "Yamaha", logo: "/assets/img/partner-yamaha.webp" },
  { name: "Mercainox", logo: "/assets/img/partner-mercainox.webp" },
  { name: "Fox LED", logo: "/assets/img/partner-foxled.webp" },
  { name: "Real Taberna", logo: "/assets/img/partner-realtaberna.webp" },
  { name: "Piloto", logo: "/assets/img/partner-piloto.webp" },
  { name: "PT Store", logo: "/assets/img/partner-ptstore.webp" },
  { name: "Nectarina", logo: "/assets/img/partner-nectarina.webp" },
  { name: "Heineken", logo: "/assets/img/partner-heineken.webp" },
  { name: "Just Fit", logo: "/assets/img/partner-justfit.webp" },
];

// Shuffle array randomly
function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export default function Partners() {
  const [showModal, setShowModal] = useState(false);
  const [partners, setPartners] = useState(fallbackPartners);
  const [shuffledPartners, setShuffledPartners] = useState([]);
  const [randomOffset, setRandomOffset] = useState(0);
  const marqueeRef = useRef(null);

  // Carregar parceiros da API (geridos no backoffice)
  useEffect(() => {
    fetch(`${API_URL}/api/partners.php`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setPartners(data.map((p) => ({ name: p.name, logo: p.logo_url })));
        }
      })
      .catch(() => {});
    setRandomOffset(Math.random() * 100);
  }, []);

  // Shuffle partners for "slot machine" effect
  useEffect(() => {
    setShuffledPartners(shuffleArray(partners));
  }, [partners]);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") setShowModal(false);
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, []);

  const displayPartners = shuffledPartners.length > 0 ? shuffledPartners : partners;

  return (
    <>
      <section id="partners" data-testid="partners-section" className="py-16 bg-[#0d1320] border-y border-white/5">
        {/* Patrocínio de campos */}
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 mb-14" data-testid="court-sponsorship">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
            <div>
              <span className="font-body text-xs font-medium uppercase tracking-widest text-jp-lime">Patrocínio de Campos</span>
              <h2 className="font-display text-3xl sm:text-4xl text-white mt-3 mb-4 leading-tight">
                Sê dono de um campo.<br />
                <span className="text-jp-lime italic">Dá-lhe o nome da tua empresa.</span>
              </h2>
              <p className="font-body text-sm text-white/50 leading-relaxed mb-6 max-w-md">
                Cada campo do Just Padel Center pode ter o nome e a imagem da empresa que o patrocina — visível em cada jogo, torneio e fotografia. Uma montra permanente para a tua marca!
              </p>
              <a
                href="#contacts"
                onClick={() => window.dispatchEvent(new CustomEvent("prefill-contact-subject", { detail: "patrocinio-campo" }))}
                className="inline-flex items-center gap-2 bg-jp-lime text-[#0a0f1a] font-body font-semibold text-sm px-6 py-3 rounded-full hover:brightness-110 transition-all"
                data-testid="court-sponsorship-cta"
              >
                Quero o meu campo
              </a>
            </div>
            <img
              src="/assets/img/patrocinio-campos.webp"
              alt="Vista aérea dos campos do Just Padel Center com os logos das empresas patrocinadoras"
              width="1440" height="1085"
              loading="lazy"
              className="w-full h-auto rounded-2xl border border-white/10"
              data-testid="court-sponsorship-image"
            />
          </div>
        </div>

        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 mb-6">
          <span className="font-body text-xs font-medium uppercase tracking-widest text-jp-lime">Parceiros</span>
        </div>
        <div className="overflow-hidden">
          <div 
            ref={marqueeRef}
            className="flex animate-marquee cursor-pointer"
            style={{ animationDelay: `-${randomOffset}s` }}
            onClick={() => setShowModal(true)}
            data-testid="partners-marquee"
          >
            {[...displayPartners, ...displayPartners, ...displayPartners].map((p, i) => (
              <div 
                key={i} 
                className="flex-shrink-0 w-44 h-20 mx-3 rounded-xl overflow-hidden hover:scale-105 transition-transform" 
                data-testid={`partner-logo-${i}`}
              >
                <img 
                  src={p.logo} 
                  alt={p.name}
                  className="w-full h-full object-contain rounded-xl"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 mt-4">
          <button 
            onClick={() => setShowModal(true)}
            className="font-body text-xs text-white/50 hover:text-jp-lime transition-colors"
            data-testid="view-all-partners-btn"
          >
            Ver todos os parceiros →
          </button>
        </div>
      </section>

      {/* Modal with all partners */}
      {showModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => setShowModal(false)}
          data-testid="partners-modal-overlay"
        >
          <div 
            className="bg-[#0d1320] border border-white/10 rounded-2xl p-8 max-w-4xl w-full max-h-[80vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
            data-testid="partners-modal"
          >
            {/* Close button */}
            <button 
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              data-testid="partners-modal-close"
            >
              <X size={20} className="text-white" />
            </button>

            {/* Header */}
            <div className="mb-8">
              <span className="font-body text-xs font-medium uppercase tracking-widest text-jp-lime mb-2 block">Os Nossos</span>
              <h2 className="font-display text-3xl text-white font-medium">Parceiros</h2>
            </div>

            {/* Partners grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {partners.map((p, i) => (
                <div 
                  key={i} 
                  className="rounded-xl overflow-hidden aspect-[3/2] hover:scale-105 transition-transform"
                  data-testid={`modal-partner-${i}`}
                >
                  <img 
                    src={p.logo} 
                    alt={p.name}
                    className="w-full h-full object-contain rounded-xl"
                  />
                </div>
              ))}
            </div>

            {/* Footer text */}
            <p className="font-body text-white/30 text-sm text-center mt-8">
              Obrigado aos nossos parceiros pelo apoio contínuo!
            </p>
          </div>
        </div>
      )}
    </>
  );
}
